import { useState } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Loader2, Mail, KeyRound } from 'lucide-react'

export function AccountSecurityCard({ email }: { email: string | null }) {
  const { toast } = useToast()

  const [newEmail, setNewEmail] = useState(email ?? '')
  const [savingEmail, setSavingEmail] = useState(false)

  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' })
  const [savingPwd, setSavingPwd] = useState(false)

  const handleChangeEmail = async () => {
    if (!newEmail || newEmail === email) return
    setSavingEmail(true)
    try {
      const { error } = await supabase.auth.updateUser({ email: newEmail })
      if (error) throw error
      toast({ title: 'Confirmation sent', description: `Check ${newEmail} to confirm the change. Your current email stays active until then.` })
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' })
    } finally {
      setSavingEmail(false)
    }
  }

  const handleChangePassword = async () => {
    if (!pwd.next || pwd.next !== pwd.confirm) {
      toast({ title: 'Passwords do not match', variant: 'destructive' })
      return
    }
    if (pwd.next.length < 6) {
      toast({ title: 'Password must be at least 6 characters', variant: 'destructive' })
      return
    }
    setSavingPwd(true)
    try {
      if (email && pwd.current) {
        const { error: signInErr } = await supabase.auth.signInWithPassword({ email, password: pwd.current })
        if (signInErr) throw new Error('Current password is incorrect')
      }
      const { error } = await supabase.auth.updateUser({ password: pwd.next })
      if (error) throw error
      toast({ title: 'Password updated' })
      setPwd({ current: '', next: '', confirm: '' })
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' })
    } finally {
      setSavingPwd(false)
    }
  }

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Account</CardTitle></CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2">
          <Label className="flex items-center gap-1.5 text-xs"><Mail className="w-3.5 h-3.5" /> Email address</Label>
          <div className="flex gap-2">
            <Input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} className="max-w-xs" />
            <Button size="sm" disabled={savingEmail || !newEmail || newEmail === email} onClick={handleChangeEmail}>
              {savingEmail && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />} Update
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">You'll receive a confirmation link at the new address before it takes effect.</p>
        </div>

        <div className="space-y-2 pt-2 border-t">
          <Label className="flex items-center gap-1.5 text-xs"><KeyRound className="w-3.5 h-3.5" /> Change password</Label>
          <div className="grid sm:grid-cols-3 gap-2 max-w-xl">
            <Input type="password" placeholder="Current password" value={pwd.current} onChange={e => setPwd(p => ({ ...p, current: e.target.value }))} />
            <Input type="password" placeholder="New password" value={pwd.next} onChange={e => setPwd(p => ({ ...p, next: e.target.value }))} />
            <Input type="password" placeholder="Confirm new password" value={pwd.confirm} onChange={e => setPwd(p => ({ ...p, confirm: e.target.value }))} />
          </div>
          <Button size="sm" variant="outline" disabled={savingPwd || !pwd.next} onClick={handleChangePassword}>
            {savingPwd && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />} Change password
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
